import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

// The server returns channels the member already has a grant on first (see
// core-modules `getAssignableChannels`), so permission lists surface existing
// access at the top without any client-side sorting.
export const GetAssignableChannels = graphql(/* GraphQL */ `
  query GetAssignableChannels($input: AssignableChannelsInput!) {
    assignableChannels(input: $input) {
      currentProductAccessLevels {
        publish
        comments
      }
      channel {
        id
        name
        avatar
        service
      }
    }
  }
`)

type GetAssignableChannelsResult = ResultOf<typeof GetAssignableChannels>

type AssignableChannelResult = NonNullable<
  GetAssignableChannelsResult['assignableChannels']
>[number]

export interface AssignableChannel {
  currentAccessLevel: AssignableChannelResult['currentProductAccessLevels']['publish']
  channel: {
    id: string
    name: string
    avatar: string
    service: AssignableChannelResult['channel']['service']
  }
}

interface UseAssignableChannelsReturn {
  assignableChannels: AssignableChannel[] | null
  loading: boolean
  error: boolean
}

export const useAssignableChannels = ({
  organizationId,
  accountId,
}: {
  organizationId: string
  accountId?: string
}): UseAssignableChannelsReturn => {
  const { data, loading, error } = useQuery(GetAssignableChannels, {
    variables: {
      input: {
        organizationId,
        accountId,
      },
    },
    skip: !organizationId,
    fetchPolicy: 'network-only',
  })

  // The team UI is single-dropdown per channel, so surface the `publish`
  // product as the current access level (the per-product UI is a separate
  // task). Memoise so the reference stays stable across renders — consumers
  // feed this into form-init effects, which must not re-fire (and reset edits)
  // just because the dialog re-rendered.
  const assignableChannels = useMemo<AssignableChannel[] | null>(() => {
    if (!data?.assignableChannels) {
      return null
    }
    return data.assignableChannels.map((assignable) => ({
      currentAccessLevel: assignable.currentProductAccessLevels.publish,
      channel: assignable.channel,
    }))
  }, [data])

  return {
    assignableChannels,
    loading,
    error: !!error,
  }
}
